import React, { useState, useMemo } from 'react';
import { message, Progress, Select, Button, Tag, Modal, Table, Popconfirm, Spin, Input, Divider } from 'antd';
import { fetchStandaloneBlock, deleteTranslatedBlock, getMetaTags, deleteMetaTag } from '../../utils/services/complexDocumentsAPI';

const { Option } = Select;
const { Search } = Input;

const CONCURRENCY_LIMIT = 10;

const runWithConcurrency = async (items, concurrency, processFn) => {
  let index = 0;
  const runNext = async () => {
    while (index < items.length) {
      const currentIndex = index++;
      await processFn(items[currentIndex], currentIndex);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, () => runNext()),
  );
};

const getBlockId = (record) => record.parentBlockId || record.id || record.blockId;
const getBlockName = (record) => record.parentBlockName || record.name || record.blockName || 'Unnamed';

const ManageBlocksModal = ({ visible, onClose, categories }) => {
  const [categoryId, setCategoryId] = useState(null);
  const [fetchedBlocks, setFetchedBlocks] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [fetchingBlocks, setFetchingBlocks] = useState(false);
  const [deletingBlocks, setDeletingBlocks] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [metadataKey, setMetadataKey] = useState('');
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [metadataProgress, setMetadataProgress] = useState(null);

  const filteredBlocks = useMemo(() => {
    if (!searchText.trim()) return fetchedBlocks;
    const query = searchText.trim().toLowerCase();
    return fetchedBlocks.filter((b) => {
      const name = getBlockName(b).toLowerCase();
      const commId = (b.communicationId || '').toLowerCase();
      return name.includes(query) || commId.includes(query);
    });
  }, [fetchedBlocks, searchText]);

  const isBusy = fetchingBlocks || deletingBlocks || updatingMetadata;

  const handleClose = () => {
    setCategoryId(null);
    setFetchedBlocks([]);
    setSelectedKeys([]);
    setDeleteProgress(null);
    setFetchingBlocks(false);
    setDeletingBlocks(false);
    setSearchText('');
    setMetadataKey('');
    setUpdatingMetadata(false);
    setMetadataProgress(null);
    onClose();
  };

  const handleFetchBlocks = async () => {
    if (!categoryId) {
      message.error('Please select a category');
      return;
    }
    setFetchingBlocks(true);
    setFetchedBlocks([]);
    setSelectedKeys([]);
    setDeleteProgress(null);
    setSearchText('');
    try {
      const result = await fetchStandaloneBlock({
        categoryId,
        limit: 16000,
      });
      const blocks = Array.isArray(result?.data) ? result.data :
                     Array.isArray(result) ? result : [];
      setFetchedBlocks(blocks);
      if (blocks.length === 0) {
        message.info('No blocks found for this category');
      } else {
        message.success(`Fetched ${blocks.length} block(s)`);
      }
    } catch (error) {
      console.error('Error fetching blocks:', error);
      message.error('Failed to fetch blocks');
    } finally {
      setFetchingBlocks(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      setDeletingBlocks(true);
      await deleteTranslatedBlock(blockId);
      setFetchedBlocks((prev) => prev.filter((b) => getBlockId(b) !== blockId));
      setSelectedKeys((prev) => prev.filter((k) => k !== blockId));
      message.success('Block deleted successfully');
    } catch (error) {
      console.error('Error deleting block:', error);
      message.error(`Failed to delete block: ${error.message}`);
    } finally {
      setDeletingBlocks(false);
    }
  };

  const performBulkDelete = async (ids) => {
    setDeletingBlocks(true);
    setDeleteProgress({ completed: 0, total: ids.length, failed: 0 });
    let completed = 0;
    let failed = 0;
    const failedIds = [];

    await runWithConcurrency(ids, CONCURRENCY_LIMIT, async (blockId) => {
      try {
        await deleteTranslatedBlock(blockId);
        completed++;
      } catch (error) {
        console.error(`Failed to delete block ${blockId}:`, error);
        failed++;
        failedIds.push(blockId);
      }
      setDeleteProgress({ completed: completed + failed, total: ids.length, failed });
    });

    setFetchedBlocks((prev) => prev.filter((b) => failedIds.includes(getBlockId(b))));
    setSelectedKeys([]);
    setDeletingBlocks(false);

    if (failed === 0) {
      message.success(`Successfully deleted ${completed} block(s)`);
    } else {
      message.warning(`Deleted ${completed} block(s), ${failed} failed`);
    }
    setDeleteProgress(null);
  };

  const handleDeleteSelected = () => {
    if (selectedKeys.length === 0) {
      message.warning('No blocks selected');
      return;
    }
    performBulkDelete(selectedKeys);
  };

  const handleDeleteAll = () => {
    if (fetchedBlocks.length === 0) {
      message.warning('No blocks to delete');
      return;
    }
    performBulkDelete(fetchedBlocks.map((b) => getBlockId(b)));
  };

  const handleSelectAll = () => {
    const allVisibleKeys = filteredBlocks.map((b) => getBlockId(b));
    setSelectedKeys(allVisibleKeys);
  };

  const handleDeselectAll = () => {
    setSelectedKeys([]);
  };

  const handleRemoveMetadata = async () => {
    const key = metadataKey.trim();
    if (!key) {
      message.error('Please enter a metadata key');
      return;
    }
    if (selectedKeys.length === 0) {
      message.warning('No blocks selected');
      return;
    }

    const selectedBlocks = fetchedBlocks.filter((b) => selectedKeys.includes(getBlockId(b)));
    const uniqueGroupIds = [...new Set(selectedBlocks.map((b) => b.groupId).filter(Boolean))];

    if (uniqueGroupIds.length === 0) {
      message.warning('Selected blocks have no group IDs');
      return;
    }

    setUpdatingMetadata(true);
    setMetadataProgress({ completed: 0, total: uniqueGroupIds.length, found: 0, removed: 0, failed: 0 });

    let completed = 0;
    let found = 0;
    let removed = 0;
    let failed = 0;

    await runWithConcurrency(uniqueGroupIds, CONCURRENCY_LIMIT, async (groupId) => {
      try {
        const metaResponse = await getMetaTags('block-document', groupId);
        const metaTags = Array.isArray(metaResponse?.data) ? metaResponse.data : [];
        const hasKey = metaTags.some((tag) => tag.key === key);

        if (hasKey) {
          found++;
          await deleteMetaTag('block-document', groupId, key);
          removed++;
        }
        completed++;
      } catch (error) {
        console.error(`Failed to remove metadata '${key}' from group ${groupId}:`, error);
        failed++;
        completed++;
      }
      setMetadataProgress({ completed, total: uniqueGroupIds.length, found, removed, failed });
    });

    setUpdatingMetadata(false);

    if (failed === 0 && removed > 0) {
      message.success(`Removed '${key}' from ${removed} of ${uniqueGroupIds.length} block group(s)`);
    } else if (removed === 0 && failed === 0) {
      message.info(`Key '${key}' was not found in any of the ${uniqueGroupIds.length} block group(s)`);
    } else {
      message.warning(`Removed: ${removed}, Not found: ${uniqueGroupIds.length - removed - failed}, Failed: ${failed}`);
    }
    setMetadataProgress(null);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'parentBlockName',
      key: 'name',
      ellipsis: true,
      render: (_, record) => getBlockName(record),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      width: 100,
      render: (text) => <Tag>{(text || '-').toUpperCase()}</Tag>,
    },
    {
      title: 'Communication ID',
      dataIndex: 'communicationId',
      key: 'communicationId',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Group ID',
      dataIndex: 'groupId',
      key: 'groupId',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      render: (_, record) => (
        <Popconfirm
          title="Delete this block?"
          onConfirm={() => handleDeleteBlock(getBlockId(record))}
          okText="Yes"
          cancelText="No"
          disabled={isBusy}
        >
          <Button type="danger" size="small" disabled={isBusy}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys) => setSelectedKeys(keys),
    getCheckboxProps: () => ({ disabled: isBusy }),
  };

  const allVisibleSelected =
    filteredBlocks.length > 0 &&
    filteredBlocks.every((b) => selectedKeys.includes(getBlockId(b)));

  return (
    <Modal
      title="Manage Blocks"
      visible={visible}
      onCancel={handleClose}
      width={960}
      centered
      destroyOnClose
      footer={null}
      maskClosable={!isBusy}
      closable={!isBusy}
    >
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Select Category:
        </label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Select
            style={{ flex: 1 }}
            placeholder="Select a category to fetch blocks"
            value={categoryId}
            onChange={(value) => {
              setCategoryId(value);
              setFetchedBlocks([]);
              setSelectedKeys([]);
              setDeleteProgress(null);
              setSearchText('');
            }}
            disabled={isBusy}
            showSearch
            optionFilterProp="children"
          >
            {categories.map((category) => (
              <Option key={category.id} value={category.id}>
                {category.name || `Category ${category.id}`}
              </Option>
            ))}
          </Select>
          <Button
            type="primary"
            onClick={handleFetchBlocks}
            loading={fetchingBlocks}
            disabled={!categoryId || deletingBlocks || updatingMetadata}
          >
            Fetch Blocks
          </Button>
        </div>
      </div>

      {fetchedBlocks.length > 0 && (
        <>
          <div style={{ marginBottom: '12px' }}>
            <Search
              placeholder="Search by block name or communication ID"
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={isBusy}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontWeight: 'bold' }}>
              {searchText.trim()
                ? `${filteredBlocks.length} of ${fetchedBlocks.length} block(s) shown`
                : `${fetchedBlocks.length} block(s) found`}
              {selectedKeys.length > 0 && (
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  {selectedKeys.length} selected
                </Tag>
              )}
            </span>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                size="small"
                onClick={allVisibleSelected ? handleDeselectAll : handleSelectAll}
                disabled={isBusy || filteredBlocks.length === 0}
              >
                {allVisibleSelected ? 'Deselect All' : 'Select All'}
              </Button>
              <Popconfirm
                title={`Delete ${selectedKeys.length} selected block(s)?`}
                onConfirm={handleDeleteSelected}
                okText="Yes, Delete"
                cancelText="Cancel"
                disabled={selectedKeys.length === 0 || isBusy}
              >
                <Button
                  type="primary"
                  size="small"
                  disabled={selectedKeys.length === 0 || isBusy}
                >
                  Delete Selected ({selectedKeys.length})
                </Button>
              </Popconfirm>
              <Popconfirm
                title={`Delete ALL ${fetchedBlocks.length} block(s) in this category? This cannot be undone.`}
                onConfirm={handleDeleteAll}
                okText="Yes, Delete All"
                cancelText="Cancel"
                disabled={isBusy}
                okType="danger"
              >
                <Button type="danger" size="small" disabled={isBusy}>
                  Delete All ({fetchedBlocks.length})
                </Button>
              </Popconfirm>
            </div>
          </div>

          <Divider orientation="left" style={{ margin: '12px 0' }}>Update Metadata</Divider>

          <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Input
              placeholder="Metadata key to remove (e.g. filename)"
              value={metadataKey}
              onChange={(e) => setMetadataKey(e.target.value)}
              disabled={isBusy}
              style={{ width: 280 }}
            />
            <Popconfirm
              title={`Remove metadata key '${metadataKey.trim()}' from ${selectedKeys.length} selected block(s)?`}
              onConfirm={handleRemoveMetadata}
              okText="Yes, Remove"
              cancelText="Cancel"
              disabled={selectedKeys.length === 0 || !metadataKey.trim() || isBusy}
            >
              <Button
                disabled={selectedKeys.length === 0 || !metadataKey.trim() || isBusy}
                loading={updatingMetadata}
              >
                Remove Metadata ({selectedKeys.length})
              </Button>
            </Popconfirm>
            {selectedKeys.length === 0 && fetchedBlocks.length > 0 && (
              <span style={{ fontSize: '12px', color: '#999' }}>Select blocks first</span>
            )}
          </div>

          {metadataProgress && (
            <div style={{ marginBottom: '12px' }}>
              <Progress
                percent={Math.round((metadataProgress.completed / metadataProgress.total) * 100)}
                status={metadataProgress.failed > 0 ? 'exception' : 'active'}
                format={() => `${metadataProgress.completed}/${metadataProgress.total}`}
              />
              <span style={{ fontSize: '12px', color: '#666' }}>
                Found: {metadataProgress.found} | Removed: {metadataProgress.removed}
                {metadataProgress.failed > 0 && (
                  <span style={{ color: '#ff4d4f' }}> | Failed: {metadataProgress.failed}</span>
                )}
              </span>
            </div>
          )}

          <Divider style={{ margin: '12px 0' }} />

          {deleteProgress && (
            <div style={{ marginBottom: '12px' }}>
              <Progress
                percent={Math.round((deleteProgress.completed / deleteProgress.total) * 100)}
                status={deleteProgress.failed > 0 ? 'exception' : 'active'}
                format={() => `${deleteProgress.completed}/${deleteProgress.total}`}
              />
              {deleteProgress.failed > 0 && (
                <span style={{ color: '#ff4d4f', fontSize: '12px' }}>
                  {deleteProgress.failed} failed
                </span>
              )}
            </div>
          )}

          <Table
            dataSource={filteredBlocks}
            columns={columns}
            rowKey={(record) => getBlockId(record)}
            rowSelection={rowSelection}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
            scroll={{ y: 400 }}
            loading={isBusy}
          />
        </>
      )}

      {fetchingBlocks && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" tip="Fetching blocks..." />
        </div>
      )}

      {!fetchingBlocks && fetchedBlocks.length === 0 && categoryId && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          Click "Fetch Blocks" to load blocks for the selected category.
        </div>
      )}
    </Modal>
  );
};

export default ManageBlocksModal;
